import type {
  FilterValue,
  GetTagStatisticsData,
  OverviewTableData,
  GetTagStatisticsReturn,
  TagSource,
  Tag,
  PhysicalProduct,
} from '../type';
import { createPortal, unstable_batchedUpdates } from 'react-dom';
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { cx } from '@emotion/css';
import { useTranslate } from '@tolgee/react';
import { DownloadOutlined, FullscreenExitOutlined, FullscreenOutlined } from '@ant-design/icons';
import { Button, message, Radio, Table, TableProps, Spin } from 'antd';
import { formatNumber } from 'utils/numberFormat';
import { useRequest, useSize } from 'ahooks';
import { useLevel1Tag } from '../tagLevel';
import { getTagStatistics, getTagStatisticsPage, markTagExport } from '../service';
import { browserDownload } from 'utils/download';
import { get, map, groupBy } from 'lodash-es';
import { v4 as uuidV4 } from 'uuid';
import { initEcharts, EChartsOption } from 'lib/echarts';
import { mapValues, toPairs, keys, flatten, last, isEmpty } from 'lodash-es';
import { useTagSourceLabel } from '../useLabel';
import { DateSelect, DateTargetSelect } from './index';
import useSmartField from 'hooks/useSmartField';
import { useTagTransName } from '../util';
import { ProductNameRender } from '../Product';

interface OverviewProps {
  values: FilterValue;
}
export const Overview = memo((props: OverviewProps) => {
  const { values } = props;
  const queryData = useMemo<GetTagStatisticsData>(() => {
    const storeIds = map(get(values, 'storeInfo.list'), 'id') as unknown as number[];
    const source = get(values, 'source');
    const tagId = flatten(
      map(get(values, 'tagId'), (o) => {
        const lastId = last(o)!;
        if (typeof lastId === 'string') {
          return get(values, `tagIdMap.${lastId}`)!;
        }
        return lastId;
      }),
    );
    const data = {
      fromDate:
        values.pickerType === 'date'
          ? values.dateRange?.[0]?.format('YYYY-MM-DD')
          : values.monthRange?.[0]?.format('YYYY-MM'),
      toDate:
        values.pickerType === 'date'
          ? values.dateRange?.[1]?.format('YYYY-MM-DD')
          : values.monthRange?.[1]?.format('YYYY-MM'),
      dateTarget: values.dateTarget,
      departmentId: map(get(values, 'departments'), 'id'),
      platform: values.platform === 'ALL' ? undefined : values.platform,
      storeType: get(values, 'storeType'),
      storeIds: storeIds?.length ? storeIds : undefined,
      source: source?.length ? source : undefined,
      lastOperatorId: get(values, 'lastOperatorId'),
      spuClassName:
        values.keywordType === 'spuClassName' && values.keyword ? values.keyword : undefined,
      spuId: values.keywordType === 'spuId' && values.keyword ? values.keyword : undefined,
      physicalProductName:
        values.keywordType === 'physicalProductName' && values.keyword ? values.keyword : undefined,
      sellerSku: values.keywordType === 'sellerSku' && values.keyword ? values.keyword : undefined,
      isReviewUpdated: get(values, 'isReviewUpdated'),
      tagId: isEmpty(tagId) ? undefined : tagId,
    };
    return data;
  }, [values]);
  const { data, loading } = useRequest(() => getTagStatistics(queryData), {
    refreshDeps: [queryData],
    ready: !!queryData,
    debounceWait: 300,
  });

  return (
    <div>
      <Spin spinning={loading}>
        <>
          <Level1Count dataSource={data} />
          <Chart dataSource={data} />
        </>
      </Spin>
      <TableView queryData={queryData} />
    </div>
  );
});

Overview.displayName = 'Overview';

type Level1CountProps = {
  dataSource?: GetTagStatisticsReturn;
};

const Level1Count = (props: Level1CountProps) => {
  const level1 = useLevel1Tag();
  const { dataSource } = props;
  const array = Object.entries(level1).map(([value, label]) => {
    return {
      name: label,
      value,
    };
  });
  const containerRef = useRef<HTMLDivElement>(null!);
  const width = useSize(containerRef)?.width || 0;
  const blockMinWidth = 128;
  const blockCount = Math.floor((width + 16) / (blockMinWidth + 16));
  const tagMap = useMemo(() => {
    return groupBy(dataSource, 'tagName');
  }, [dataSource]);

  return (
    <div className="flex gap-[16px] my-[16px] flex-wrap" ref={containerRef}>
      {array.map((each) => {
        const valueLength = get(tagMap, `${each.value}.length`, 0) as number;
        return (
          <div
            key={each.name}
            className="box-border border border-solid border-[#d3d7e0] rounded-[4px] bg-[#fff] px-[16px] py-[13px]"
            style={{
              flexBasis: `calc(${100 / blockCount}% - ${(16 * (blockCount - 1)) / blockCount}px)`,
              minWidth: `${blockMinWidth}px`,
              flexGrow: array.length > blockCount ? 0 : 1,
            }}
          >
            <div className="leading-[20px] text-[#515359]">{each.name}</div>
            <div className="leading-[22px] mt-[4px] font-medium text-[16px]">
              {formatNumber(valueLength, '0,0')}
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface ChartProps {
  dataSource?: GetTagStatisticsReturn;
}
const Chart = (props: ChartProps) => {
  const { t } = useTranslate();
  const { dataSource } = props;
  const class1 =
    'h-[56px] py-[17px] pl-[24px] text-[#232426] text-[16px] font-medium border-solid border-0 border-b border-[#f0f0f0]';
  const [visible, setVisible] = useState(false);
  const [type, setType] = useState<'bar' | 'line'>('bar');
  const { field, helpers } = useSmartField<FilterValue['dateTarget']>('dateTarget');

  return (
    <div className="flex">
      <div className="mr-[16px] bg-[#fff] flex-grow">
        <div className={cx('flex items-center justify-between', class1)}>
          <span>{t('bs_58')}</span>
          <Button
            onClick={() => setVisible(true)}
            className="!mr-[24px]"
            type="link"
            icon={<FullscreenOutlined />}
          >
            {t('bs_80')}
          </Button>
        </div>
        <div className="px-[24px] py-[12px]">
          <Radio.Group
            value={type}
            onChange={(e) => {
              setType(e.target.value);
            }}
          >
            <Radio.Button value="bar">{t('bs_60')}</Radio.Button>
            <Radio.Button value="line">{t('bs_61')}</Radio.Button>
          </Radio.Group>
          <BarBrush dataSource={dataSource} type={type} />
        </div>
      </div>
      <div className="bg-[#fff] w-[426px] flex-shrink-0">
        <div className={class1}>{t('bs_59')}</div>
        <Pie dataSource={dataSource} />
      </div>

      <FullScreen
        visible={visible}
        title={t('bs_56')}
        onClose={() => setVisible(false)}
        className="left-[16px] right-[16px] top-[16px] bottom-0"
        getContainer={() => {
          const id = document.getElementById('beisi-tag-container')!;
          return id.parentElement!;
        }}
      >
        <div className="bg-[#fff] h-full flex flex-col">
          <div className="flex items-center justify-between px-[24px] py-[16px]">
            <Radio.Group
              value={type}
              onChange={(e) => {
                setType(e.target.value);
              }}
            >
              <Radio.Button value="bar">{t('bs_60')}</Radio.Button>
              <Radio.Button value="line">{t('bs_61')}</Radio.Button>
            </Radio.Group>

            <div className="inline-flex gap-[16px]">
              <DateTargetSelect value={field.value} onChange={(value) => helpers.setValue(value)} />
              <DateSelect />
            </div>
          </div>

          <div className="flex-grow flex items-center px-[24px] justify-center">
            <BarBrush dataSource={dataSource} type={type} className="w-[85%]" />
          </div>
        </div>
      </FullScreen>
    </div>
  );
};

const BarBrush = (props: {
  dataSource?: GetTagStatisticsReturn;
  type: 'bar' | 'line';
  className?: string;
}) => {
  const { dataSource, className, type } = props;
  const chartDomRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts>();
  const { width } = useSize(chartDomRef) || {};
  const level1 = useLevel1Tag();
  const options = useMemo<EChartsOption>(() => {
    const dayArray = keys(groupBy(dataSource, 'date'));
    const typeArray = map(toPairs(level1), ([value, label]) => {
      return {
        name: label,
        value,
      };
    });
    const groupData = mapValues(groupBy(dataSource, 'tagName'), (o) =>
      mapValues(groupBy(o, 'date'), (oo) => oo.length),
    );

    const data = map(typeArray, (each) => {
      return {
        ...each,
        data: map(dayArray, (day) => {
          return get(groupData, `${each.value}.${day}`, 0);
        }),
      };
    });

    return {
      color: [
        '#73A0FA',
        '#73DEB3',
        '#7585A2',
        '#F7C739',
        '#EB7E65',
        '#83D0EF',
        '#A285D2',
        '#FFAB67',
      ],
      legend: {
        data: map(typeArray, 'name'),
      },
      tooltip: {},
      xAxis: {
        data: dayArray,
        name: '',
        axisLine: { onZero: true },
        splitLine: { show: false },
        splitArea: { show: false },
      },
      yAxis: {
        splitNumber: 1,
      },
      grid: {
        left: 30,
        bottom: 30,
      },
      series:
        type === 'bar'
          ? map(data, (each) => {
              return {
                name: each.name,
                type: 'bar',
                stack: 'all',
                barMaxWidth: 20,
                data: each.data,
              };
            })
          : map(data, (each) => {
              return {
                name: each.name,
                type: 'line',
                data: each.data,
              };
            }),
    };
  }, [dataSource, type, level1]);
  useEffect(() => {
    chartRef.current?.resize();
  }, [width]);

  useEffect(() => {
    chartRef.current?.setOption(options, true);
  }, [options]);

  useLayoutEffect(() => {
    chartRef.current = initEcharts(chartDomRef.current!);
  }, []);
  return <div ref={chartDomRef} className={cx('mt-[16px] h-[400px]', className)}></div>;
};

const Pie = (props: { dataSource?: GetTagStatisticsReturn }) => {
  const { dataSource } = props;
  const chartDomRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts>();
  const { width } = useSize(chartDomRef) || {};
  const sourceLabelMap = useTagSourceLabel();
  const options = useMemo<EChartsOption>(() => {
    const defaultData: Record<TagSource, number> = {
      CONVERSATION: 0,
      ORDER: 0,
      REVIEW: 0,
      TODO: 0,
    };
    const group = mapValues(groupBy(dataSource, 'source'), (o) => o.length);
    const data = map(toPairs({ ...defaultData, ...group }), ([key, count]) => {
      return {
        name: sourceLabelMap[key as TagSource],
        value: count,
      };
    });

    return {
      color: [
        '#73A0FA',
        '#73DEB3',
        '#7585A2',
        '#F7C739',
        '#EB7E65',
        '#83D0EF',
        '#A285D2',
        '#FFAB67',
      ],
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
      },
      legend: {
        top: 0,
        left: 'center',
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          label: {
            show: true,
            position: 'inside',
            formatter: function (params) {
              if (params.value === 0) {
                return ''; // 如果值为0，则不显示标签
              } else {
                return formatNumber(Number(params.percent) / 100, '0.00%');
              }
            },
            color: '#fff',
          },
          labelLine: {
            show: false,
          },
          data,
        },
      ],
    };
  }, [dataSource, sourceLabelMap]);

  useEffect(() => {
    chartRef.current?.setOption(options);
  }, [options]);
  useEffect(() => {
    chartRef.current?.resize();
  }, [width]);
  useLayoutEffect(() => {
    chartRef.current = initEcharts(chartDomRef.current!);
  }, []);
  return (
    <div className="px-[24px] py-[12px]">
      <div ref={chartDomRef} className="h-[400px]"></div>
    </div>
  );
};

interface TableViewProps {
  queryData: GetTagStatisticsData;
}
const TableView = (props: TableViewProps) => {
  const { t } = useTranslate();
  const { queryData } = props;
  const [tagLevel, setTagLevel] = useState<number>(1);
  const [pageInfo, setPageInfo] = useState({
    pageNo: 1,
    pageSize: 10,
  });
  const [sorter, setSorter] = useState<{
    sort: 'itemCountDesc' | 'itemCountAsc';
  }>(undefined!);
  const params = useMemo(() => {
    return {
      ...queryData,
      tagLevel,
      page: pageInfo,
      ...sorter,
    };
  }, [queryData, pageInfo, tagLevel, sorter]);
  const { data, loading } = useRequest(() => getTagStatisticsPage(params), {
    ready: !!params,
    refreshDeps: [params],
    debounceWait: 300,
  });

  const getTagName = useTagTransName();

  const columns = useMemo<TableProps<OverviewTableData>['columns']>(() => {
    const percentLabelMap: Record<number, string> = {
      1: t('bs_68'),
      2: t('bs_69'),
      3: t('bs_70'),
    };
    const fix = [
      {
        title: t('bs_9'),
        dataIndex: 'physicalProduct',
        key: 'physicalProduct',
        render: (p: PhysicalProduct) => {
          return <ProductNameRender product={p} />;
        },
      },
      {
        title: t('bs_64'),
        dataIndex: 'firstLevelTag',
        key: 'firstLevelTag',
        render: (tag: Tag) => {
          const newTag = getTagName(tag);
          return get(newTag, '0.name');
        },
      },
      tagLevel === 2 || tagLevel === 3
        ? {
            title: t('bs_65'),
            dataIndex: 'secondLevelTag',
            key: 'secondLevelTag',
            render: (tag: Tag) => {
              const newTag = getTagName(tag);
              return get(newTag, '0.name');
            },
          }
        : undefined!,
      tagLevel === 3
        ? {
            title: t('bs_66'),
            dataIndex: 'thirdLevelTag',
            key: 'thirdLevelTag',
            render: (tag: Tag) => {
              const newTag = getTagName(tag);
              return get(newTag, '0.name');
            },
          }
        : undefined!,
      {
        title: t('bs_67'),
        dataIndex: 'itemCount',
        key: 'itemCount',
        sorter: true,
      },
      {
        title: percentLabelMap[tagLevel],
        dataIndex: 'percentage',
        key: 'percentage',
      },
    ];
    return fix.filter(Boolean);
  }, [t, tagLevel, getTagName]);

  const { runAsync: exportData, loading: exportLoading } = useRequest(
    () =>
      markTagExport({
        ...queryData,
        tagLevel,
      }),
    {
      manual: true,
      onError: () => {
        message.error(t('ViewShops.导出失败'));
      },
    },
  );

  const tableData = useMemo(() => {
    const list = get(data, 'data.data', []);
    return map(list, (each) => {
      return {
        ...each,
        uuid: uuidV4(),
      };
    });
  }, [data]);

  return (
    <div className="mt-[16px] bg-[#fff]">
      <div className="h-[56px] py-[17px] px-[24px] bg-[#fff] flex justify-between items-center border-solid border-0 border-b border-[#f0f0f0]">
        <div className="text-[#232426] text-[16px] font-medium">{t('bs_63')}</div>

        <Button
          type="ghost"
          className="ant-radio-button-wrapper-checked"
          icon={<DownloadOutlined />}
          loading={exportLoading}
          onClick={() => {
            exportData().then((res) => {
              if (res?.resultUrl) {
                browserDownload(res.resultUrl, undefined!);
              } else {
                message.error(t('ViewShops.导出失败'));
              }
            });
          }}
        >
          {t('bs_62')}
        </Button>
      </div>

      <div className="px-[24px]">
        <Radio.Group
          className="!my-[16px]"
          value={tagLevel}
          onChange={(e) => {
            unstable_batchedUpdates(() => {
              setTagLevel(e.target.value);
              setPageInfo((prevState) => {
                return {
                  ...prevState,
                  pageNo: 1,
                };
              });
            });
          }}
        >
          <Radio.Button value={1}>{t('bs_64')}</Radio.Button>
          <Radio.Button value={2}>{t('bs_65')}</Radio.Button>
          <Radio.Button value={3}>{t('bs_66')}</Radio.Button>
        </Radio.Group>

        <Table<OverviewTableData>
          loading={loading}
          dataSource={tableData}
          columns={columns}
          rowKey="uuid"
          pagination={{
            current: pageInfo.pageNo,
            pageSize: pageInfo.pageSize,
            total: get(data, 'data.page.total', 0),
            onChange: (pageNo, pageSize) => {
              setPageInfo((prevState) => {
                return {
                  ...prevState,
                  pageNo,
                  pageSize,
                };
              });
            },
            showTotal: (total) => {
              return t('feedback.共x项', { x: total });
            },
            showQuickJumper: true,
            showSizeChanger: true,
          }}
          onChange={(_p, _f, sorter) => {
            if (!Array.isArray(sorter)) {
              if (sorter?.field === 'itemCount') {
                if (sorter?.order) {
                  setSorter({
                    sort: sorter.order === 'descend' ? 'itemCountDesc' : 'itemCountAsc',
                  });
                } else {
                  setSorter(undefined!);
                }
              }
            }
          }}
        />
      </div>
    </div>
  );
};

interface FullScreenProps {
  getContainer: () => HTMLElement;
  visible: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
  className?: string;
  title?: string;
}

const FullScreen = (props: FullScreenProps) => {
  const { getContainer, visible, onClose, title, className, children } = props;
  const containerRef = useRef<HTMLElement>(null!);
  const { t } = useTranslate();
  const overflowPropertyRef = useRef<string>('');

  useLayoutEffect(() => {
    const container = getContainer();
    containerRef.current = container;
    container.style.position = 'relative';
  }, [getContainer]);
  useEffect(() => {
    if (visible === false) {
      containerRef.current.style.overflow = overflowPropertyRef.current;
    } else {
      overflowPropertyRef.current = containerRef.current.style.overflow;
      containerRef.current.style.overflow = 'hidden';
    }
  }, [visible]);
  if (!containerRef.current) {
    return null;
  }
  return visible
    ? createPortal(
        <div className={cx('absolute left-0 right-0 top-0 bottom-0 z-[999] bg-[#fff]', className)}>
          <div className="flex justify-between items-center border-0 border-b border-solid border-[rgba(0,0,0,.05)] px-[24px] py-[16px]">
            <div className="leading-[22px] text-[16px]">{title}</div>
            <Button
              className="!py-0 !h-[22px]"
              type="link"
              icon={<FullscreenExitOutlined />}
              onClick={onClose}
            >
              {t('bs_57')}
            </Button>
          </div>
          {children}
        </div>,
        containerRef.current,
      )
    : null;
};
